// src/app/modules/Visitors/pages/VisitorDetails.jsx
// Changes: skeleton loader + clean icon tab labels with counts

import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import * as actions from '../../../Dashbord/_redux/dashboard/Actions';
import { Tabs, Tab } from 'react-bootstrap';
import { FlagVisitorModal } from '../components/FlagVisitorModal';
import { AddThreatModal } from '../components/AddThreatModal';
import { BlacklistModal } from '../components/BlacklistModal';
import { VisitorDetailsSkeleton } from '../../../../../_metronic/layout/components/skeleton/SkeletonLoader';
import {
  ArrowLeft, User, Phone, Mail, Shield, TrendingUp,
  Building2, Clock, AlertTriangle, FileText, Flag,
  Ban, MapPin, Calendar, List,
} from 'lucide-react';
import Moment from 'moment';
import './visitor-details.scss';
import '../../../../../_metronic/layout/components/skeleton/detail-tabs.scss';
// import '../../../../shared/detail-tabs.scss';

export function VisitorDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();
  const isMountedRef = useRef(true);

  const { org } = useSelector((s) => ({ org: s.auth.organization }), shallowEqual);

  const [loading,      setLoading]      = useState(false);
  const [visitorData,  setVisitorData]  = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [activeTab,    setActiveTab]    = useState('overview');
  const [showFlag,     setShowFlag]     = useState(false);
  const [showThreat,   setShowThreat]   = useState(false);
  const [showBlacklist,setShowBlacklist]= useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    if (id && org?.organization_id) {
      fetchVisitorDetails();
      fetchVisitorActivity();
    }
    return () => { isMountedRef.current = false; };
  }, [id, org?.organization_id]);

  async function fetchVisitorDetails() {
    if (!isMountedRef.current) return;
    setLoading(true);
    try {
      const data = await dispatch(actions.fetchAllData('visitor_profile', id));
      if (isMountedRef.current) setVisitorData(data);
    } catch (e) { console.error(e); if (isMountedRef.current) setVisitorData(null); }
    finally     { if (isMountedRef.current) setLoading(false); }
  }

  async function fetchVisitorActivity() {
    if (!isMountedRef.current) return;
    try {
      const data = await dispatch(actions.fetchAllData('visitor_activity', id, { organization_id: org?.organization_id }));
      if (isMountedRef.current) setActivityData(data);
    } catch (e) { console.error(e); }
  }

  const refresh = () => { fetchVisitorDetails(); fetchVisitorActivity(); };

  // ── Skeleton loader ──────────────────────────────────────────────────────
  if (loading) return <VisitorDetailsSkeleton />;

  if (!visitorData) return (
    <div className="visitor-details-page">
      <div className="vd-empty">
        <User size={48} />
        <h4>Visitor not found</h4>
        <p>This visitor profile may have been removed or doesn't exist.</p>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/visitors')}>
          <ArrowLeft size={14} className="mr-1" /> Back to Visitors
        </button>
      </div>
    </div>
  );

  const profile           = visitorData.profile || visitorData;
  const access_logs       = activityData?.access_logs     || [];
  const threat_alerts     = activityData?.threat_alerts   || [];
  const blacklist_entries = activityData?.blacklist_entries || [];
  const is_blacklisted    = activityData?.is_blacklisted  || false;

  const visitorName = profile?.known_names?.[0]?.name || profile?.visitor_name || 'Unknown Visitor';
  const riskLevel   = profile?.risk_level || 'low';
  const riskScore   = profile?.risk_score || 0;

  const getRiskBadge = () => {
    if (riskScore < 30) return 'low';
    if (riskScore < 50) return 'medium';
    if (riskScore < 70) return 'high';
    return 'critical';
  };

  // Reusable tab label with optional count pill
  const TabLabel = ({ icon: Icon, label, count }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Icon size={13} />
      {label}
      {count !== undefined && (
        <span className="tab-count">{count}</span>
      )}
    </span>
  );

  return (
    <>
      <div className="visitor-details-page">

        {/* ── Header ── */}
        <div className="vd-header">
          <button className="vd-back" onClick={() => navigate('/visitors')}>
            <ArrowLeft size={16} />
          </button>
          <div className="vd-header__text">
            <div className="vd-breadcrumb">
              <span>Visitors</span><span className="sep">/</span>
              <span className="active">{visitorName}</span>
            </div>
            <h1>Visitor Profile</h1>
          </div>
        </div>

        {/* ── Main grid ── */}
        <div className="vd-main">

          {/* Sidebar */}
          <div className="vd-sidebar">
            <div className="vd-sidebar-card">
              <div className="vd-hero">
                <div className="vd-avatar"><User size={28} /></div>
                <div className="vd-name">{visitorName}</div>
                <div className="vd-id">{(profile?.profile_id || id)?.slice(0, 10)}…</div>
                {profile?.flagged_at && (
                  <span className="vd-flagged-badge"><Flag size={10} />Flagged</span>
                )}
              </div>

              <div className="vd-risk">
                <div className="vd-risk__title">Risk Assessment</div>
                <div className="vd-risk__body">
                  <div className="vd-risk__score">
                    <span className="num">{riskScore}</span>
                    <span className="max">/100</span>
                  </div>
                  <div className="vd-risk__info">
                    <span className={`vd-risk__badge vd-risk__badge--${getRiskBadge()}`}>
                      <Shield size={10} /> {riskLevel.toUpperCase()} RISK
                    </span>
                    <div className="vd-risk__sub">
                      {riskScore < 30 ? 'No concerns detected'
                        : riskScore < 50 ? 'Standard verification advised'
                        : riskScore < 70 ? 'Additional verification required'
                        : 'Caution — verify identity'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="vd-contact">
                <div className="vd-contact__title">Contact Information</div>
                {profile?.visitor_phone && <div className="vd-contact__item"><Phone size={13} />{profile.visitor_phone}</div>}
                {profile?.visitor_email && <div className="vd-contact__item"><Mail  size={13} />{profile.visitor_email}</div>}
                {!profile?.visitor_phone && !profile?.visitor_email && (
                  <div className="vd-contact__empty">No contact information</div>
                )}
              </div>

              <div className="vd-stats">
                <div className="vd-stats__title">Quick Statistics</div>
                {[
                  { icon: <TrendingUp    size={14} style={{ color: '#5341C4' }} />, value: profile?.visit_statistics?.total_visits || 0,              label: 'Total Visits' },
                  { icon: <Building2     size={14} style={{ color: '#2563EB' }} />, value: profile?.visit_statistics?.unique_organizations || 0,       label: 'Locations Visited' },
                  { icon: <AlertTriangle size={14} style={{ color: '#D97706' }} />, value: profile?.incident_history?.total_incidents || 0,             label: 'Incidents' },
                  { icon: <Clock         size={14} style={{ color: '#16a34a' }} />, value: profile?.visit_statistics?.last_visit ? Moment(profile.visit_statistics.last_visit).fromNow() : 'Never', label: 'Last Visit' },
                ].map((s, i) => (
                  <div key={i} className="vd-stats__item">
                    <div className="stat-icon">{s.icon}</div>
                    <div>
                      <div className="stat-value">{s.value}</div>
                      <div className="stat-label">{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Content col */}
          <div className="vd-content-col">
            <div className="vd-details-card">
              <Tabs activeKey={activeTab} onSelect={setActiveTab} className="vd-tabs">

                <Tab eventKey="overview" title={<TabLabel icon={User} label="Overview" />}>
                  <div className="vd-tab-body">

                    {profile?.known_names?.length > 0 && (
                      <div className="vd-section">
                        <div className="vd-section__head"><User size={15} /><h3>Known Names & Aliases</h3></div>
                        <div className="vd-names-grid">
                          {profile.known_names.map((n, i) => (
                            <div key={i} className="vd-name-card">
                              <div className="vd-name-card__name">{n.name}</div>
                              <div className="vd-name-card__count">Used {n.occurrence_count}×</div>
                              <div className="vd-name-card__date">Last: {Moment(n.last_seen).format('MMM DD, YYYY')}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {profile?.risk_factors?.length > 0 && (
                      <div className="vd-section">
                        <div className="vd-section__head"><Shield size={15} /><h3>Risk Factors</h3></div>
                        <div className="vd-risk-factors">
                          {profile.risk_factors.map((f, i) => (
                            <div key={i} className="vd-risk-factor">
                              <AlertTriangle size={14} />
                              <div>
                                <div className="vd-risk-factor__text">{f.factor}</div>
                                <div className="vd-risk-factor__weight">Weight: {f.weight}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {profile?.visit_statistics && (
                      <div className="vd-section">
                        <div className="vd-section__head"><Calendar size={15} /><h3>Visit Timeline</h3></div>
                        <div className="vd-timeline-summary">
                          {[
                            { label: 'First Visit',   value: profile.visit_statistics.first_visit ? Moment(profile.visit_statistics.first_visit).format('MMM DD, YYYY') : 'N/A' },
                            { label: 'Last Visit',    value: profile.visit_statistics.last_visit  ? Moment(profile.visit_statistics.last_visit).format('MMM DD, YYYY')  : 'N/A' },
                            { label: 'Total Visits',  value: profile.visit_statistics.total_visits || 0 },
                          ].map((b, i) => (
                            <div key={i} className="vd-timeline-box">
                              <div className="vd-timeline-box__label">{b.label}</div>
                              <div className="vd-timeline-box__value">{b.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {profile?.notes && (
                      <div className="vd-section">
                        <div className="vd-section__head"><FileText size={15} /><h3>Notes</h3></div>
                        <div className="vd-notes">{profile.notes}</div>
                      </div>
                    )}
                  </div>
                </Tab>

                <Tab eventKey="visits" title={<TabLabel icon={List} label="Visit History" count={access_logs.length} />}>
                  <div className="vd-tab-body">
                    {access_logs.length === 0 ? (
                      <div className="vd-empty-state">
                        <Building2 size={40} />
                        <h4>No Visit History</h4>
                        <p>This visitor hasn't checked in yet.</p>
                      </div>
                    ) : (
                      <div className="vd-timeline">
                        {access_logs.map((log, i) => (
                          <div key={i} className="vd-timeline-item">
                            <div className="vd-tl-dot"><Building2 size={12} /></div>
                            <div className="vd-tl-card">
                              <div className="vd-tl-card__header">
                                <h5>{log.purpose || 'Visit'}</h5>
                                <span className={`vd-log-status vd-log-status--${log.status}`}>{log.status}</span>
                              </div>
                              <div className="vd-tl-card__meta">
                                <span><Calendar size={11} />{Moment(log.entry_time).format('MMM DD, YYYY [at] h:mm A')}</span>
                                {log.duration_minutes && <span><Clock size={11} />{log.duration_minutes}m</span>}
                              </div>
                              {log.host_name && <div className="vd-tl-card__detail"><User size={11} />Here to see: <strong>{log.host_name}</strong></div>}
                              {log.location  && <div className="vd-tl-card__detail"><MapPin size={11} />{log.location}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Tab>

                <Tab eventKey="alerts" title={<TabLabel icon={AlertTriangle} label="Alerts" count={threat_alerts.length} />}>
                  <div className="vd-tab-body">
                    {threat_alerts.length === 0 ? (
                      <div className="vd-empty-state">
                        <Shield size={40} />
                        <h4>No Threat Alerts</h4>
                        <p>No security threats have been reported for this visitor.</p>
                      </div>
                    ) : (
                      <div className="vd-alerts">
                        {threat_alerts.map((alert, i) => (
                          <div key={i} className={`vd-alert-card vd-alert-card--${alert.severity}`}>
                            <div className="vd-alert-card__header">
                              <Shield size={15} />
                              <h5>{alert.alert_type.replace(/_/g, ' ').toUpperCase()}</h5>
                              <span className={`vd-severity vd-severity--${alert.severity}`}>{alert.severity}</span>
                            </div>
                            <div className="vd-alert-card__date">{Moment(alert.created_at).format('MMM DD, YYYY [at] h:mm A')}</div>
                            <div className="vd-alert-card__desc">{alert.description}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Tab>

                {is_blacklisted && blacklist_entries.length > 0 && (
                  <Tab eventKey="blacklist" title={<TabLabel icon={Ban} label="Blacklist" count={blacklist_entries.length} />}>
                    <div className="vd-tab-body">
                      <div className="vd-blacklist-banner">
                        <Ban size={18} />
                        <div>
                          <h4>This visitor is currently blacklisted</h4>
                          <p>Access will be automatically denied at all entry points.</p>
                        </div>
                      </div>
                      <div className="vd-blacklist-entries">
                        {blacklist_entries.map((entry, i) => (
                          <div key={i} className="vd-blacklist-card">
                            <div className="vd-blacklist-card__header">
                              <h5>Blacklist Entry</h5>
                              <span>Added {Moment(entry.created_at).format('MMM DD, YYYY')}</span>
                            </div>
                            <div className="vd-blacklist-card__reason"><strong>Reason:</strong> {entry.reason}</div>
                            {entry.expires_at && <div className="vd-blacklist-card__expiry"><strong>Expires:</strong> {Moment(entry.expires_at).format('MMM DD, YYYY')}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </Tab>
                )}

              </Tabs>
            </div>
          </div>
        </div>
      </div>

      <FlagVisitorModal   show={showFlag}      onHide={() => setShowFlag(false)}      profileId={id} onSuccess={refresh} />
      <AddThreatModal     show={showThreat}    onHide={() => setShowThreat(false)}    profileId={id} organizationId={org?.organization_id} onSuccess={refresh} />
      <BlacklistModal     show={showBlacklist} onHide={() => setShowBlacklist(false)} profileId={id} organizationId={org?.organization_id} onSuccess={refresh} />
    </>
  );
}